import React from 'react';

const SkeletonBase = ({ className = '' }) => (
  <div className={`shimmer rounded-lg ${className}`} />
);

export const SkeletonText = ({ width = 'w-full', height = 'h-4', className = '' }) => (
  <SkeletonBase className={`${width} ${height} ${className}`} />
);

export const SkeletonCircle = ({ size = 'w-10 h-10', className = '' }) => (
  <SkeletonBase className={`${size} rounded-full ${className}`} />
);

export const SkeletonCard = ({ className = '' }) => (
  <div className={`card-3d p-6 space-y-4 ${className}`}>
    <div className="flex items-center gap-4">
      <SkeletonCircle />
      <div className="flex-1 space-y-2">
        <SkeletonText width="w-3/4" />
        <SkeletonText width="w-1/2" height="h-3" />
      </div>
    </div>
    <SkeletonText height="h-20" />
  </div>
);

export const SkeletonStatCard = ({ count = 4 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="stat-card space-y-3" style={{ animationDelay: `${i * 100}ms` }}>
        <div className="flex items-center justify-between">
          <SkeletonText width="w-24" height="h-3" />
          <SkeletonCircle size="w-10 h-10" />
        </div>
        <SkeletonText width="w-20" height="h-8" />
        <SkeletonText width="w-32" height="h-3" />
      </div>
    ))}
  </div>
);

export const SkeletonTable = ({ rows = 5, cols = 5 }) => (
  <div className="glass rounded-2xl overflow-hidden border border-gray-100 dark:border-dark-700">
    <div className="p-4 border-b border-gray-100 dark:border-dark-700">
      <div className="flex gap-4">
        {Array.from({ length: cols }).map((_, i) => (
          <SkeletonText key={i} width="flex-1" height="h-4" />
        ))}
      </div>
    </div>
    <div className="divide-y divide-gray-100 dark:divide-dark-700">
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="p-4 flex items-center gap-4">
          <SkeletonCircle size="w-8 h-8" />
          {Array.from({ length: cols - 1 }).map((_, col) => (
            <SkeletonText key={col} width="flex-1" height="h-4" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonList = ({ items = 6 }) => (
  <div className="space-y-3">
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="glass rounded-xl p-4 flex items-center gap-4">
        <SkeletonCircle />
        <div className="flex-1 space-y-2">
          <SkeletonText width="w-2/3" />
          <SkeletonText width="w-1/3" height="h-3" />
        </div>
        <SkeletonText width="w-16" height="h-8" />
      </div>
    ))}
  </div>
);

export const SkeletonPage = () => (
  <div className="space-y-6 animate-fade-in">
    <div className="space-y-2">
      <SkeletonText width="w-48" height="h-8" />
      <SkeletonText width="w-72" height="h-4" />
    </div>
    <SkeletonStatCard count={4} />
    <SkeletonTable rows={6} cols={5} />
  </div>
);

export const SkeletonGrid = ({ items = 6, cols = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' }) => (
  <div className={`grid ${cols} gap-4 md:gap-6`}>
    {Array.from({ length: items }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

const SkeletonLoader = ({ variant = 'page', ...props }) => {
  switch (variant) {
    case 'card':
      return <SkeletonCard {...props} />;
    case 'stat':
      return <SkeletonStatCard {...props} />;
    case 'table':
      return <SkeletonTable {...props} />;
    case 'list':
      return <SkeletonList {...props} />;
    case 'grid':
      return <SkeletonGrid {...props} />;
    case 'page':
    default:
      return <SkeletonPage {...props} />;
  }
};

export default SkeletonLoader;
